// Локальные удаления, о которых сервер ещё не знает.
//
// ─── Класс проблемы ─────────────────────────────────────────────────────────
// Удаление сущностей идёт через PowerSync: строка гасится в локальной SQLite
// (`softDeleteRow`), а upload-очередь уносит правку на сервер ФОНОМ. Списки же
// частью серверные (tRPC: агрегаты, курсорная пагинация, фильтры по `set_items`
// — Библиотека, пикеры артефактов). Дальше происходит гонка, которую нельзя
// выиграть инвалидацией: `invalidate()` сразу после мутации рефетчит сервер,
// где строка ЕЩЁ жива, и элемент возвращается на экран. Сервер об удалении
// узнаёт через секунду-другую, но сказать об этом вкладке некому —
// PowerSync-write не эмитит realtime-событий, — поэтому список исправляется
// только при следующем монтировании или перезагрузке страницы. Ровно это
// видел владелец: материал ушёл в Корзину, но остался в Библиотеке (2026-08-21).
//
// ─── Решение ────────────────────────────────────────────────────────────────
// То же, что уже сделано в Корзине (`useUnifiedTrashList`), но с обратным
// знаком: серверный список — основа, локальный слой PowerSync — поправка.
// Корзина ПРИБАВЛЯЕТ к серверному списку локальные tombstone'ы; обычный список
// их ВЫЧИТАЕТ. Свойства решения:
//
//   - мгновенно: локальный `deleted_at` виден в тот же тик;
//   - переживает перемонтирование и офлайн (в отличие от оптимистичной правки
//     кэша TanStack Query, которую сносит первый же рефетч);
//   - самозарастает: стримы отдают только живые строки, поэтому после
//     подтверждения удаления tombstone уезжает и поправка обнуляется сама —
//     ни двойного вычитания, ни «залипшего» скрытого элемента.
//
// Обратный случай (создание, о котором сервер ещё не знает) сюда НЕ относится:
// локально созданная строка попадает в серверный список сразу после upload'а,
// а до него её показывают офлайн-слои своих разделов.

import { useMemo } from "react";
import { usePowerSyncQuery } from "@/lib/powersync/use-query";

/**
 * Таблицы с soft-delete, которые синкает PowerSync И которые показываются
 * серверными списками. `null` в местах вызова означает «сущность не синкается»
 * (эссе, визуальные разборы) — там поправку взять неоткуда.
 */
export type SoftDeletedTable =
	| "materials"
	| "folders"
	| "notes"
	| "tests"
	| "flashcard_sets"
	| "glossary_terms"
	| "audio_recaps"
	| "visual_analyses";

const EMPTY: ReadonlySet<string> = new Set<string>();

/**
 * Список таблиц закрыт типом — имя подставляется в SQL, параметром его не сделать.
 *
 * `userId` приходит аргументом, а не из `useCurrentUser()`: `lib/` — лист графа
 * зависимостей и `hooks/` не импортирует (гейт `web-lib-is-a-leaf`). Та же
 * конвенция у соседнего `use-local-trash-query.ts`.
 */
export function usePendingDeletions(
	table: SoftDeletedTable | null,
	userId: string | undefined,
): ReadonlySet<string> {
	const scopedUserId = userId ?? "";
	const sql = table
		? `SELECT id FROM ${table} WHERE user_id = ? AND deleted_at IS NOT NULL`
		: // Заглушка сохраняет форму запроса (один `?`), чтобы число параметров
			// совпадало и без таблицы: правила хуков не дают вызвать запрос условно.
			"SELECT id FROM materials WHERE user_id = ? AND 0";
	const query = usePowerSyncQuery<{ id: string }>(sql, [scopedUserId]);
	const rows = query.data;

	return useMemo(() => {
		if (!table || scopedUserId === "" || !rows || rows.length === 0) return EMPTY;
		return new Set(rows.map((r) => r.id));
	}, [rows, table, scopedUserId]);
}
